// @flow

import * as React from 'react';

import { NavigableListItem } from '@ta/list.navigable-list';

import type { FetcherResult } from '../Typeahead';
import BasicResult, { type PaddingStyle, type IconStyle } from './BasicResult';
import TypeaheadTracking, { type UserInteractionType } from '../TypeaheadTracking';
import type { TypeaheadResult, TypeaheadResultType } from '../TypeaheadResult';
import getIconFromResultType from './iconFromResultType';
import type { AddressDisplay } from './addressDisplay';
import formatAddress from './addressDisplay';

type Props = {|
  /** The TypeaheadJsonFetcher result */
  result: FetcherResult,

  /** The typeahead tracker */
  tracker: TypeaheadTracking,

  /** An optional callback if a result is selected */
  onSelection?: (
    event: SyntheticEvent<HTMLElement> | KeyboardEvent,
    result: TypeaheadResult,
  ) => void,

  /** An optional callback for if/when the mouse moves over the result */
  onMouseMove?: (event: SyntheticMouseEvent<HTMLElement>) => mixed,

  /** True if this result is active. Defaults to false */
  active?: boolean,

  /** True if we should include an icon to match the result type */
  includeIcon?: boolean,

  /** Display mode for address subtitle of results.
   * If no display mode is provided, there will be no address displayed in subtitle */
  addressDisplay: AddressDisplay,

  /** Padding style for the result */
  paddingStyle?: PaddingStyle,

  /** Optionally customize the icon color of icons in the result items. */
  iconStyle?: IconStyle,

  /** Optionally specify the typeahead item type, default to be "location_id" */
  itemType: TypeaheadResultType,

  /** result position in result list, starting at 0 */
  resultIndex?: number,
|};

/**
 * Create a basic typeahead search result from /TypeaheadJson?action=API. Selecting a result of this type will trigger a
 * call to onSelection() with a TypeaheadResult of { type: "location_id", value: <geo_id> }
 *
 * Note that this is intended to be used inside a <NavigableList/>
 */
class TypeaheadJsonResult extends React.Component<Props> {
  static defaultProps = {
    onSelection: undefined,
    onMouseMove: undefined,
    active: false,
    includeIcon: false,
    addressDisplay: 'parent_geos',
    paddingStyle: undefined,
    itemType: 'location_id',
    iconStyle: undefined,
    resultIndex: undefined,
  };

  onSelect = (event: SyntheticEvent<HTMLElement> | KeyboardEvent) => {
    const userInteractionType: UserInteractionType = event.type === 'click' ? 'click' : 'enter';
    const { tracker, onSelection, itemType, result, resultIndex } = this.props;

    // ETL Trackers
    const trackingData = { resultIndex, ...result };
    tracker.trackSelection(trackingData, userInteractionType);
    tracker.trackEventViaMEVTCookie(trackingData, userInteractionType);

    const { value: geoId, scope, name } = result; // TODO: change this to be a generic id and not a 'geoId'
    if (onSelection) {
      onSelection(event, {
        type: itemType,
        value: geoId,
        displayValue: name,
        scope,
      });
    }
  };

  render() {
    const details: { [string]: any } = this.props.result.details || {};
    const title = details.name;

    const subtitle = formatAddress(
      this.props.addressDisplay,
      details.parent_name,
      details.grandparent_name,
      details.address,
    );
    const icon = this.props.includeIcon ? getIconFromResultType(this.props.result.type) : '';

    return (
      <NavigableListItem
        onSelection={this.onSelect}
        onMouseMove={this.props.onMouseMove}
        active={this.props.active}
      >
        <BasicResult
          title={title}
          subtitle={subtitle}
          iconName={icon}
          active={this.props.active}
          paddingStyle={this.props.paddingStyle}
          iconStyle={this.props.iconStyle}
        />
      </NavigableListItem>
    );
  }
}

export default TypeaheadJsonResult;
